import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users as UsersIcon, UserPlus, Mail, Trash2, Search, RefreshCw, Ban, CheckCircle } from 'lucide-react';
import { users, orgs } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { TableSkeleton } from '../components/Skeleton';
import type { Operator, UserRole, OrgType } from '../types';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

const ROLE_BADGES: Record<UserRole, string> = {
  admin: 'bg-purple-500/15 text-purple-400',
  engineer: 'bg-blue-500/15 text-blue-400',
  operator: 'bg-emerald-500/15 text-emerald-400',
  viewer: 'bg-gray-500/15 text-gray-400',
};

export default function UsersPage() {
  const { user, getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('operator');
  const [selectedOrg, setSelectedOrg] = useState<string>(user?.org_id ?? '');

  // Search state with debounce
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ConfirmDialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<'disable' | 'delete' | null>(null);
  const [pendingOp, setPendingOp] = useState<Operator | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const canManageUsers = user?.role === 'admin';
  const canInvite = user?.role === 'admin' || user?.role === 'engineer';
  const showOrgSelector = user?.org_type === 'kinetica' || user?.org_type === 'integrator';

  // Helper for authenticated API calls to endpoints not in client.ts
  const authFetch = async (path: string, opts: RequestInit = {}) => {
    const token = await getAccessToken();
    const res = await fetch(`${API_BASE}${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...opts.headers,
      },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`API ${res.status}: ${text}`);
    }
    return res.json();
  };

  const { data: operators = [], isLoading, error: usersError } = useQuery({
    queryKey: ['users', debouncedSearch],
    queryFn: async () => {
      const token = await getAccessToken();
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      const qs = params.toString();
      const url = `${API_BASE}/users${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      return res.json() as Promise<Operator[]>;
    },
  });

  const { data: orgList = [] } = useQuery({
    queryKey: ['orgs'],
    queryFn: orgs.list,
    enabled: showOrgSelector,
  });

  // For integrators: own org + child orgs
  const visibleOrgs = user?.org_type === 'kinetica'
    ? orgList
    : orgList.filter((o) => o.org_id === user?.org_id || o.parent_org_id === user?.org_id);

  const orgsByType: Record<OrgType, typeof orgList> = {
    kinetica: visibleOrgs.filter((o) => o.org_type === 'kinetica'),
    integrator: visibleOrgs.filter((o) => o.org_type === 'integrator'),
    client: visibleOrgs.filter((o) => o.org_type === 'client'),
  };

  const selectedOrgObj = orgList.find((o) => o.org_id === selectedOrg);
  const selectedOrgType = selectedOrgObj?.org_type;

  const inviteMutation = useMutation({
    mutationFn: () =>
      users.invite(
        inviteEmail,
        inviteRole,
        {
          ...(inviteName ? { name: inviteName } : {}),
          ...(selectedOrg && selectedOrg !== user?.org_id
            ? { org_id: selectedOrg, org_type: selectedOrgType }
            : {}),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowInvite(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('operator');
      setSelectedOrg(user?.org_id ?? '');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ sub, role }: { sub: string; role: string }) => users.updateRole(sub, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (sub: string) => authFetch(`/users/${sub}/resend`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const disableMutation = useMutation({
    mutationFn: (sub: string) => authFetch(`/users/${sub}/disable`, { method: 'PUT' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const enableMutation = useMutation({
    mutationFn: (sub: string) => authFetch(`/users/${sub}/enable`, { method: 'PUT' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (sub: string) => users.revoke(sub),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleDisable = (op: Operator) => {
    setPendingOp(op);
    setDialogAction('disable');
    setDialogOpen(true);
  };

  const handleEnable = (op: Operator) => {
    enableMutation.mutate(op.sub);
  };

  const handleDelete = (op: Operator) => {
    setPendingOp(op);
    setDialogAction('delete');
    setDialogOpen(true);
  };

  const handleDialogConfirm = () => {
    if (!pendingOp) return;
    if (dialogAction === 'disable') {
      disableMutation.mutate(pendingOp.sub, {
        onSettled: () => {
          setDialogOpen(false);
          setPendingOp(null);
          setDialogAction(null);
        },
      });
    } else if (dialogAction === 'delete') {
      revokeMutation.mutate(pendingOp.sub, {
        onSettled: () => {
          setDialogOpen(false);
          setPendingOp(null);
          setDialogAction(null);
        },
      });
    }
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setPendingOp(null);
    setDialogAction(null);
  };

  if (usersError) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-400 mb-2">Failed to load users</p>
        <p className="text-xs text-gray-500">{(usersError as Error).message}</p>
        <button onClick={() => window.location.reload()} className="btn-primary mt-4 text-sm">
          Reload Page
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">User Management</h2>
        {canInvite && (
          <button onClick={() => setShowInvite(!showInvite)} className="btn-primary text-sm flex items-center gap-2">
            <UserPlus size={14} /> Invite User
          </button>
        )}
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="input w-full pl-9"
            placeholder="Search by email..."
          />
        </div>
      </div>

      {/* Invite form */}
      {showInvite && (
        <div className="card mb-4">
          <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
            <Mail size={14} /> Invite Operator
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              inviteMutation.mutate();
            }}
            className="flex gap-3 items-end flex-wrap"
          >
            <div className="flex-1 min-w-40">
              <label className="block text-xs text-gray-500 mb-1">Email</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="input w-full"
                placeholder="colleague@company.com"
                required
              />
            </div>
            <div className="flex-1 min-w-32">
              <label className="block text-xs text-gray-500 mb-1">Name <span className="text-gray-600">(optional)</span></label>
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                className="input w-full"
                placeholder="Full name"
              />
            </div>
            <div className="w-36">
              <label className="block text-xs text-gray-500 mb-1">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as UserRole)}
                className="input w-full"
              >
                <option value="viewer">Viewer</option>
                <option value="operator">Operator</option>
                <option value="engineer">Engineer</option>
                {user?.role === 'admin' && <option value="admin">Admin</option>}
              </select>
            </div>
            {showOrgSelector && (
              <div className="w-56">
                <label className="block text-xs text-gray-500 mb-1">Organisation</label>
                <select
                  value={selectedOrg}
                  onChange={(e) => setSelectedOrg(e.target.value)}
                  className="input w-full"
                >
                  {user?.org_type === 'kinetica' ? (
                    <>
                      {orgsByType.kinetica.length > 0 && (
                        <optgroup label="Kinetica">
                          {orgsByType.kinetica.map((o) => (
                            <option key={o.org_id} value={o.org_id}>
                              {o.org_name} ({o.org_type})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {orgsByType.integrator.length > 0 && (
                        <optgroup label="Integrators">
                          {orgsByType.integrator.map((o) => (
                            <option key={o.org_id} value={o.org_id}>
                              {o.org_name} ({o.org_type})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {orgsByType.client.length > 0 && (
                        <optgroup label="Clients">
                          {orgsByType.client.map((o) => (
                            <option key={o.org_id} value={o.org_id}>
                              {o.org_name} ({o.org_type})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  ) : (
                    visibleOrgs.map((o) => (
                      <option key={o.org_id} value={o.org_id}>
                        {o.org_name} ({o.org_type})
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}
            <button type="submit" className="btn-primary" disabled={inviteMutation.isPending}>
              {inviteMutation.isPending ? 'Sending...' : 'Send Invite'}
            </button>
          </form>
        </div>
      )}

      {/* Users table */}
      <div className="card overflow-hidden p-0 overflow-x-auto">
        {isLoading ? (
          <table className="w-full">
            <tbody>
              <TableSkeleton columns={6} rows={5} />
            </tbody>
          </table>
        ) : operators.length === 0 ? (
          <div className="p-8 text-center">
            <UsersIcon className="mx-auto mb-3 text-gray-600" size={32} />
            <p className="text-sm text-gray-400">
              {debouncedSearch ? 'No users match your search' : 'No users found'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">Name</th>
                <th className="table-header table-cell text-left">Email</th>
                <th className="table-header table-cell text-left">Role</th>
                <th className="table-header table-cell text-left">Status</th>
                <th className="table-header table-cell text-left">Last Login</th>
                {canManageUsers && <th className="table-header table-cell" />}
              </tr>
            </thead>
            <tbody>
              {operators.map((op: Operator) => {
                const isSelf = op.sub === user?.sub;
                const isDisabled = op.status === 'disabled';
                const isInvited = op.status === 'invited';
                return (
                  <tr
                    key={op.sub}
                    className={`hover:bg-surface-2/50 ${isDisabled ? 'opacity-50' : ''}`}
                  >
                    <td className="table-cell text-sm text-gray-200">{op.name}</td>
                    <td className="table-cell text-xs text-gray-400">{op.email}</td>
                    <td className="table-cell">
                      {canManageUsers && !isSelf && !isDisabled ? (
                        <select
                          value={op.role}
                          onChange={(e) =>
                            updateRoleMutation.mutate({ sub: op.sub, role: e.target.value })
                          }
                          className="input py-0.5 text-xs w-28"
                          disabled={updateRoleMutation.isPending}
                        >
                          <option value="viewer">Viewer</option>
                          <option value="operator">Operator</option>
                          <option value="engineer">Engineer</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className={`badge ${ROLE_BADGES[op.role]}`}>{op.role}</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span
                        className={`badge ${
                          op.status === 'active'
                            ? 'bg-emerald-500/15 text-emerald-400'
                            : op.status === 'invited'
                              ? 'bg-amber-500/15 text-amber-400'
                              : 'bg-red-500/15 text-red-400'
                        }`}
                      >
                        {op.status}
                      </span>
                    </td>
                    <td className="table-cell text-xs text-gray-500">
                      {op.last_login ?? 'Never'}
                    </td>
                    {canManageUsers && (
                      <td className="table-cell text-right">
                        {!isSelf && (
                          <div className="flex items-center justify-end gap-1">
                            {/* Resend invite for invited users */}
                            {isInvited && (
                              <button
                                onClick={() => resendMutation.mutate(op.sub)}
                                disabled={resendMutation.isPending}
                                className="p-1 text-gray-500 hover:text-amber-400 transition-colors"
                                title="Resend invitation"
                              >
                                <RefreshCw size={14} />
                              </button>
                            )}
                            {/* Disable / Enable toggle */}
                            {isDisabled ? (
                              <button
                                onClick={() => handleEnable(op)}
                                disabled={enableMutation.isPending}
                                className="p-1 text-gray-500 hover:text-emerald-400 transition-colors"
                                title="Enable user"
                              >
                                <CheckCircle size={14} />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDisable(op)}
                                disabled={disableMutation.isPending}
                                className="p-1 text-gray-500 hover:text-amber-400 transition-colors"
                                title="Disable user"
                              >
                                <Ban size={14} />
                              </button>
                            )}
                            {/* Permanent delete */}
                            <button
                              onClick={() => handleDelete(op)}
                              disabled={revokeMutation.isPending}
                              className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                              title="Delete permanently"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog
        open={dialogOpen && dialogAction !== null}
        title={
          dialogAction === 'disable'
            ? `Disable ${pendingOp?.name || pendingOp?.email}?`
            : `Delete ${pendingOp?.name || pendingOp?.email}?`
        }
        message={
          dialogAction === 'disable'
            ? 'They will not be able to sign in. You can re-enable them at any time.'
            : 'This action cannot be undone. The user will be permanently removed from the organisation.'
        }
        severity={dialogAction === 'disable' ? 'warning' : 'danger'}
        confirmLabel={dialogAction === 'disable' ? 'Disable' : 'Delete'}
        loading={dialogAction === 'disable' ? disableMutation.isPending : revokeMutation.isPending}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />
    </div>
  );
}
